import React from 'react';

const DateTimeField = ({
  label,
  name,
  value,
  onChange,
  type = 'datetime-local',
  required = false,
  disabled = false,
  error,
  helpText,
  min,
  max,
  className = '',
  ...props
}) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value || null });
  };

  const fieldId = `datetime-${name}`;
  const hasError = Boolean(error);

  // Convert database timestamp to datetime-local format if needed
  const formatValueForInput = (inputValue) => {
    if (!inputValue) return '';
    
    if (type === 'datetime-local') {
      // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:MM)
      const date = new Date(inputValue);
      if (isNaN(date.getTime())) return '';
      
      // Format to local datetime string
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    
    if (type === 'date') {
      // Convert to date format (YYYY-MM-DD)
      const date = new Date(inputValue);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
    
    if (type === 'time') {
      // Convert to time format (HH:MM)
      const date = new Date(inputValue);
      if (isNaN(date.getTime())) return '';
      
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${hours}:${minutes}`;
    }
    
    return inputValue;
  };

  const formatMaxForInput = (maxValue) => {
    if (!maxValue) return undefined;
    
    if (type === 'datetime-local') {
      // Current date/time for max restriction
      return new Date().toISOString().slice(0, 16);
    }
    
    if (type === 'date') {
      return new Date().toISOString().slice(0, 10);
    }
    
    return maxValue;
  };
  
  const baseInputClasses = `
    w-full px-3 py-2 border rounded-lg shadow-sm transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    disabled:bg-gray-100 disabled:cursor-not-allowed
    ${hasError 
      ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300 bg-white hover:border-gray-400'
    }
  `;

  // Helper text for different field types
  const getHelperText = () => {
    if (helpText) return helpText;
    
    switch (type) {
      case 'datetime-local':
        return 'Select the date and time when the event occurred';
      case 'date':
        return 'Select the date';
      case 'time':
        return 'Select the time';
      default:
        return '';
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={fieldId}
          className={`block text-sm font-medium ${hasError ? 'text-red-700' : 'text-gray-700'}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* DateTime Input */}
      <div className="relative">
        <input
          id={fieldId}
          name={name}
          type={type}
          value={formatValueForInput(value)}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          min={min}
          max={max || formatMaxForInput(max)}
          className={baseInputClasses.trim()}
          {...props}
        />

        {/* Calendar/Clock Icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {type === 'time' ? (
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )}
        </div>
      </div>

      {/* Current Date/Time Helper */}
      {type === 'datetime-local' && !value && (
        <button
          type="button"
          onClick={() => {
            const now = new Date().toISOString().slice(0, 16);
            onChange({ [name]: now });
          }}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Use current date and time
        </button>
      )}

      {/* Help Text */}
      {getHelperText() && !hasError && (
        <p className="text-sm text-gray-500">{getHelperText()}</p>
      )}

      {/* Error Message */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {/* Future Date Warning */}
      {type === 'datetime-local' && value && new Date(value) > new Date() && (
        <p className="text-sm text-amber-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Warning: Event date is in the future
        </p>
      )}
    </div>
  );
};

export default DateTimeField;
